/**
 * PosSoundSettings — Sonidos y avisos del POS (toggles, volumen y mensajes).
 *
 * QUÉ HACE: Configura por negocio los avisos del POS: beep al agregar/quitar,
 *   aviso de pedido nuevo (mesa / domicilio / local) con sonido, **voz** (lee el
 *   mensaje personalizado) y **notificación del sistema**, además del volumen y
 *   los mensajes de texto libre por tipo de pedido. Persiste en
 *   `lib/audio/posSoundPrefs` (ACID) y escucha cambios externos.
 * QUÉ NO DEBE HACER: No cobra ni conoce reglas de caja; solo prueba los tonos y
 *   la voz con los helpers compartidos (`appSounds`).
 */
import { useEffect, useState } from "react";
import {
  Bell,
  BellRing,
  Mic,
  MinusCircle,
  PlusCircle,
  Volume2,
} from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import { SwitchRow } from "@/components/ui/Switch";
import { FloatingLabelInput } from "@/components/ui/FloatingLabelInput";
import { requestMobileNotificationPermission } from "@/lib/push";
import {
  playCartAddBeep,
  playCartRemoveBeep,
  playPosPendingBeep,
  speak,
} from "@/lib/audio/appSounds";
import {
  DEFAULT_POS_SOUND_PREFS,
  MAX_POS_ALERT_MESSAGE_LENGTH,
  POS_SOUND_PREFS_EVENT,
  loadPosSoundPrefs,
  persistPosSoundPrefs,
  type PosSoundPrefs,
} from "@/lib/audio/posSoundPrefs";

const TEST_BUTTON_CLASS =
  "text-[10px] font-bold px-2 py-1.5 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-200 hover:border-primary-300 dark:hover:border-primary-700 hover:text-primary-700 dark:hover:text-primary-300 transition-colors";

export function PosSoundSettings() {
  const businessId = useAuthStore((s) => s.user?.businessId);
  const [hydrated, setHydrated] = useState(false);
  const [prefs, setPrefs] = useState<PosSoundPrefs>(DEFAULT_POS_SOUND_PREFS);

  useEffect(() => {
    setHydrated(false);
    setPrefs(loadPosSoundPrefs(businessId));
    setHydrated(true);
  }, [businessId]);

  useEffect(() => {
    const onExternal = () => setPrefs(loadPosSoundPrefs(businessId));
    window.addEventListener(POS_SOUND_PREFS_EVENT, onExternal);
    return () => window.removeEventListener(POS_SOUND_PREFS_EVENT, onExternal);
  }, [businessId]);

  useEffect(() => {
    if (!hydrated) return;
    const id = window.setTimeout(() => {
      const prev = loadPosSoundPrefs(businessId);
      if (JSON.stringify(prev) === JSON.stringify(prefs)) return;
      persistPosSoundPrefs(businessId, prefs);
    }, 420);
    return () => window.clearTimeout(id);
  }, [businessId, hydrated, prefs]);

  const volumePct = Math.round(prefs.volume * 100);

  return (
    <div className="space-y-2">
      <SwitchRow
        size="sm"
        icon={
          <PlusCircle
            size={16}
            className="text-slate-600 dark:text-slate-300"
          />
        }
        label="Sonido al agregar ítems"
        description="Pitido agudo al agregar al carrito, escanear o reanudar una venta."
        checked={prefs.add}
        onChange={(v) => setPrefs((p) => ({ ...p, add: v }))}
      />
      <SwitchRow
        size="sm"
        icon={
          <MinusCircle
            size={16}
            className="text-slate-600 dark:text-slate-300"
          />
        }
        label="Sonido al quitar o bajar cantidad"
        description="Pitido descendente al eliminar, bajar cantidad o poner la venta en espera."
        checked={prefs.remove}
        onChange={(v) => setPrefs((p) => ({ ...p, remove: v }))}
      />
      <SwitchRow
        size="sm"
        icon={
          <BellRing size={16} className="text-slate-600 dark:text-slate-300" />
        }
        label="Aviso al llegar pedidos nuevos"
        description="Doble tono + mensaje en la esquina al llegar una mesa pendiente, un pedido a domicilio o un pedido de tienda «local», en cualquier pantalla."
        checked={prefs.pending}
        onChange={(v) => setPrefs((p) => ({ ...p, pending: v }))}
      />
      <SwitchRow
        size="sm"
        icon={<Mic size={16} className="text-slate-600 dark:text-slate-300" />}
        label="Anunciar por voz los pedidos nuevos"
        description="Lee en voz alta tu mensaje personalizado (usa la voz del sistema; si escribes en otro idioma puede sonar con acento)."
        checked={prefs.voice}
        onChange={(v) => setPrefs((p) => ({ ...p, voice: v }))}
      />
      <SwitchRow
        size="sm"
        icon={<Bell size={16} className="text-slate-600 dark:text-slate-300" />}
        label="Notificación del sistema al llegar un pedido"
        description="Aviso del sistema (categoría Ventas) con el cliente o la mesa, además del sonido."
        checked={prefs.notify}
        onChange={(v) => {
          setPrefs((p) => ({ ...p, notify: v }));
          if (v) void requestMobileNotificationPermission();
        }}
      />

      <div className="px-3 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900 space-y-2">
        <p className="text-[10px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wide">
          Mensajes de voz y notificación
        </p>
        <FloatingLabelInput
          label="Pedido nuevo en mesa"
          hint="Ej: «Pedido nuevo en mesa»"
          value={prefs.mensajeMesa}
          maxLength={MAX_POS_ALERT_MESSAGE_LENGTH}
          onChange={(e) =>
            setPrefs((p) => ({ ...p, mensajeMesa: e.target.value }))
          }
        />
        <FloatingLabelInput
          label="Pedido nuevo a domicilio"
          hint="Ej: «Pedido nuevo a domicilio»"
          value={prefs.mensajeDomicilio}
          maxLength={MAX_POS_ALERT_MESSAGE_LENGTH}
          onChange={(e) =>
            setPrefs((p) => ({ ...p, mensajeDomicilio: e.target.value }))
          }
        />
        <FloatingLabelInput
          label="Pedido nuevo Tienda virtual"
          hint="Ej: «Pedido nuevo Tienda virtual»"
          value={prefs.mensajeLocal}
          maxLength={MAX_POS_ALERT_MESSAGE_LENGTH}
          onChange={(e) =>
            setPrefs((p) => ({ ...p, mensajeLocal: e.target.value }))
          }
        />
        <p className="text-[10px] text-gray-500 dark:text-slate-400 leading-relaxed">
          Escríbelos en tu idioma: el sistema lee exactamente ese texto (y, si
          hay datos, el cliente o la mesa). Si lo dejas vacío se usa el mensaje
          por defecto.
        </p>
      </div>

      <div className="px-3 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-xs font-bold text-gray-800 dark:text-slate-100">
            <Volume2 size={14} className="text-slate-600 dark:text-slate-300" />
            Volumen
          </span>
          <span className="text-[10px] font-bold text-gray-500 dark:text-slate-400">
            {volumePct}%
          </span>
        </div>
        <input
          type="range"
          min={10}
          max={100}
          step={5}
          value={volumePct}
          onChange={(e) =>
            setPrefs((p) => ({ ...p, volume: Number(e.target.value) / 100 }))
          }
          aria-label="Volumen de los avisos del POS"
          className="w-full mt-2 accent-primary-600"
        />
        <div className="grid grid-cols-2 gap-1.5 mt-1">
          <button
            type="button"
            className={TEST_BUTTON_CLASS}
            onClick={() => playCartAddBeep(prefs.volume)}
          >
            Probar agregar
          </button>
          <button
            type="button"
            className={TEST_BUTTON_CLASS}
            onClick={() => playCartRemoveBeep(prefs.volume)}
          >
            Probar quitar
          </button>
          <button
            type="button"
            className={TEST_BUTTON_CLASS}
            onClick={() => playPosPendingBeep(prefs.volume)}
          >
            Probar aviso
          </button>
          <button
            type="button"
            className={TEST_BUTTON_CLASS}
            onClick={() => speak(prefs.mensajeMesa, prefs.volume)}
          >
            Probar voz
          </button>
        </div>
        <p className="mt-2 text-[10px] text-gray-500 dark:text-slate-400 leading-relaxed">
          Por defecto todos los avisos están activos. Los cambios se guardan
          solos para este negocio y no afectan al resto de la app.
        </p>
      </div>
    </div>
  );
}
